/**
 * localStorage 工具函数
 * 提供安全的本地存储操作
 */

/**
 * 保存数据到 localStorage
 * @param {string} key - 存储键名
 * @param {any} value - 要保存的值
 */
export function saveToStorage(key, value) {
  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
    console.log(`✅ 保存设置成功: ${key}`, value);
  } catch (error) {
    console.error(`❌ 无法保存设置到本地存储 (${key}):`, error);
    // 静默失败，不影响用户使用
  }
}

/**
 * 从 localStorage 读取数据
 * @param {string} key - 存储键名
 * @param {any} defaultValue - 默认值
 * @returns {any} 读取的值或默认值
 */
export function loadFromStorage(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      console.log(`📝 使用默认设置: ${key}`, defaultValue);
      return defaultValue;
    }
    
    const parsed = JSON.parse(item);
    
    // 验证数据完整性
    if (parsed === null || parsed === undefined) {
      console.warn(`⚠️ 存储数据为空: ${key}，使用默认值`);
      return defaultValue;
    }
    
    console.log(`✅ 加载设置成功: ${key}`, parsed);
    return parsed;
  } catch (error) {
    console.error(`❌ 无法从本地存储读取设置 (${key}):`, error);
    
    // 尝试清除损坏的数据
    try {
      localStorage.removeItem(key);
      console.log(`🧹 已清除损坏的存储数据: ${key}`);
    } catch (cleanupError) {
      console.warn(`无法清除损坏的存储数据 (${key}):`, cleanupError);
    }
    
    return defaultValue;
  }
}

/**
 * 删除 localStorage 中的数据
 * @param {string} key - 存储键名
 */
export function removeFromStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`无法删除本地存储设置 (${key}):`, error);
  }
}

/**
 * 检查 localStorage 是否可用
 * @returns {boolean} 是否支持 localStorage
 */
export function isStorageAvailable() {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * 获取定时器默认设置
 * @returns {object} 定时器默认设置对象
 */
export function getTimerDefaultSettings() {
  return {
    hours: 0,
    minutes: 5,
    seconds: 0,
    volume: 70,
    growing: false,
    selectedMelody: 'bell',
    useVideoAlarm: false,
    videoId: '',
    videoTitle: '',
    lastUpdated: Date.now()
  };
}

/**
 * 获取定时器默认激活状态
 * @returns {object} 定时器默认激活状态对象
 */
export function getTimerDefaultActiveState() {
  return {
    active: false,
    remaining: 0,
    startTime: null,
    totalTime: 0,
    paused: false,
    savedAt: Date.now()
  };
}

// 存储键名常量
export const STORAGE_KEYS = {
  // 普通闹钟
  BASIC_ALARM_SETTINGS: 'clockonline_basic_alarm_settings',
  BASIC_ALARM_ACTIVE_STATE: 'clockonline_basic_alarm_active_state',
  
  // 收音机闹钟
  RADIO_ALARM_SETTINGS: 'clockonline_radio_alarm_settings',
  RADIO_ALARM_ACTIVE_STATE: 'clockonline_radio_alarm_active_state',
  
  // 视频闹钟
  VIDEO_ALARM_SETTINGS: 'clockonline_video_alarm_settings',
  VIDEO_ALARM_ACTIVE_STATE: 'clockonline_video_alarm_active_state',
  
  // 定时器
  TIMER_SETTINGS: 'clockonline_timer_settings',
  TIMER_ACTIVE_STATE: 'clockonline_timer_active_state',
  TIMERS: 'clockonline_timers', // 多定时器列表
  
  // 冥想定时器
  MEDITATION_SETTINGS: 'clockonline_meditation_settings',
  MEDITATION_STATS: 'clockonline_meditation_stats',
  MEDITATION_BACKGROUND: 'clockonline_meditation_background',
  
  // 世界时钟
  WORLD_CLOCK_CITIES: 'clockonline_world_clock_cities',
  
  // 用户偏好
  USER_PREFERENCES: 'clockonline_user_preferences',
  
  // 兼容性：保留旧的键名以便迁移
  ALARM_SETTINGS: 'clockonline_alarm_settings',
  ALARM_ACTIVE_STATE: 'clockonline_alarm_active_state'
};